import * as Yup from 'yup'
import { i18n } from '@fs/zion-locale'
import { namePartTypeKeys } from '../../shared/utils/helpers'

/**
 * Validation schema for Essential Information Card form.
 *
 * Validates:
 * 1. There is at least one NAME element with at least one part
 * 2. For every NAME, there is at least one non-empty name part (non-null, non-whitespace)
 * 3. The sex field cannot be empty
 * 4. When there are no name parts with valid values in a name object, sets an error on the name type input
 */

// Generate dynamic validation rules for all name part types
const generateNamePartValidation = () => {
  const namePartRules = {}

  namePartTypeKeys.forEach((partType) => {
    namePartRules[partType] = Yup.object().when('parts', (parts, schema) => {
      if (parts?.includes(partType)) {
        return schema.shape({
          elementId: Yup.string().when(['added', 'removed'], {
            is: (added, removed) => added || !removed,
            then: (s) => s.required('elementId and fieldId are both required for name parts'),
            otherwise: (s) => s.optional(),
          }),
          fieldId: Yup.string().when(['added', 'removed'], {
            is: (added, removed) => added || !removed,
            then: (s) => s.required('elementId and fieldId are both required for name parts'),
            otherwise: (s) => s.optional(),
          }),
          added: Yup.boolean().optional(),
          removed: Yup.boolean().optional(),
          type: Yup.string().optional(),
          value: Yup.string().when('removed', {
            is: (removed) => !removed,
            then: (s) => s.test('non-empty', 'Invalid name part value', (value) => value && value.trim().length > 0),
            otherwise: (s) => s.optional(),
          }),
        })
      }
      return schema.optional()
    })
  })

  return namePartRules
}

export const essentialInformationValidationSchema = Yup.object().shape({
  primaryPerson: Yup.boolean()
    .nullable()
    .test(
      'primary-person-required',
      i18n.t('essentialInformationForm.primaryPerson.required', 'Select whether this is the primary person'),
      (value) => value === true || value === false
    ),

  names: Yup.array()
    .min(1, 'At least one name is required')
    .of(
      Yup.object().shape({
        type: Yup.object()
          .shape({
            key: Yup.string().required('Name type is required'),
            primaryText: Yup.string().required('Name type is required'),
          })
          .required('Name type is required')
          .test(
            'name-has-valid-parts',
            'At least one name part must have a valid value',
            function nameHasValidParts(value, context) {
              const { parent } = context

              // Skip validation if the name is removed
              if (parent.removed) {
                return true
              }

              // Check if there are any parts defined
              if (!parent.parts || parent.parts.length === 0) {
                return this.createError({
                  message: 'At least one name part must be added',
                })
              }

              // Check if at least one part has a non-empty value
              const hasValidPart = parent.parts.some((partType) => {
                const part = parent[partType]
                return (
                  part && !part.removed && part.value && typeof part.value === 'string' && part.value.trim().length > 0
                )
              })

              if (!hasValidPart) {
                return this.createError({
                  message: 'At least one name part must have a valid value',
                })
              }

              return true
            }
          ),
        // Dynamic validation for name parts - validate if part is in parts array and not removed
        ...generateNamePartValidation(),
      })
    )
    .test(
      'at-least-one-name-with-parts',
      'At least one name with name parts is required',
      function atLeastOneNameWithParts(names) {
        if (!names || names.length === 0) {
          return this.createError({ message: 'At least one name is required' })
        }

        // Check if there's at least one name that is not removed and has parts
        const validNames = names.filter((name) => !name.removed)
        if (validNames.length === 0) {
          return this.createError({ message: 'At least one name is required' })
        }

        // Check if at least one name has at least one part
        const hasNameWithParts = validNames.some((name) => {
          if (!name.parts || name.parts.length === 0) {
            return false
          }

          // Check if at least one part has a non-empty value
          return name.parts.some((partType) => {
            const part = name[partType]
            return part && !part.removed && part.value && typeof part.value === 'string' && part.value.trim().length > 0
          })
        })

        if (!hasNameWithParts) {
          return this.createError({
            message: 'Each name must have at least one non-empty name part',
            path: 'names',
          })
        }

        return true
      }
    )
    .test(
      'each-name-has-non-empty-part',
      'Each name must have at least one non-empty name part',
      function eachNameHasNonEmptyPart(names) {
        if (!names || names.length === 0) {
          return true // This will be caught by the previous test
        }

        const validNames = names.filter((name) => !name.removed)

        for (let i = 0; i < validNames.length; i++) {
          const name = validNames[i]

          if (!name.parts || name.parts.length === 0) {
            return this.createError({
              message: 'Each name must have at least one name part',
              path: `names[${i}]`,
            })
          }

          // Check if this name has at least one non-empty part
          const hasNonEmptyPart = name.parts.some((partType) => {
            const part = name[partType]
            return part && !part.removed && part.value && typeof part.value === 'string' && part.value.trim().length > 0
          })

          if (!hasNonEmptyPart) {
            return this.createError({
              message: 'Each name must have at least one non-empty part',
              path: `names[${i}]`,
            })
          }
        }

        return true
      }
    ),
})
